import { useRequest } from 'ahooks';
import {
  Card,
  Drawer,
  List,
  message,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { useState } from 'react';
import {
  MyExamAttemptItemDTO,
  MyExamItemDTO,
  examAttemptController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import { formatDuration, optionLetter, QuestionTypeTag } from './constants';

interface Props {
  exam: MyExamItemDTO;
  onClose: () => void;
}

// 成绩抽屉：历次成绩 + 选中某次的答卷回顾（只显示对错与得分，不展示正确答案）
export default ({ exam, onClose }: Props) => {
  const { id: companyId } = useCompany();
  const [selectedAttemptId, setSelectedAttemptId] = useState<I.ID>();

  const { data: attempts, loading } = useRequest(
    () => examAttemptController.myAttempts(companyId, exam.examId as I.ID),
    {
      onSuccess: (data) => {
        const best = data?.find((item) => item.isBest);
        const first = best || data?.[data.length - 1];
        if (first?.attemptId) {
          setSelectedAttemptId(first.attemptId);
        }
      },
      onError: (e) => message.error(e.message),
    },
  );

  const { data: result, loading: resultLoading } = useRequest(
    () =>
      selectedAttemptId
        ? examAttemptController.result(companyId, selectedAttemptId)
        : Promise.resolve(undefined),
    {
      refreshDeps: [selectedAttemptId],
      onError: (e) => message.error(e.message),
    },
  );

  const attemptColumns = [
    { title: '第几次', dataIndex: 'attemptNo', width: 70 },
    { title: '得分', dataIndex: 'score', width: 70 },
    {
      title: '结果',
      dataIndex: 'pass',
      width: 80,
      render: (pass?: boolean) =>
        pass === undefined || pass === null ? (
          <Tag color="orange">作答中</Tag>
        ) : pass ? (
          <Tag color="green">通过</Tag>
        ) : (
          <Tag color="red">未通过</Tag>
        ),
    },
    {
      title: '用时',
      dataIndex: 'durationSeconds',
      width: 100,
      render: (value?: number) => formatDuration(value),
    },
    {
      title: '最高分',
      dataIndex: 'isBest',
      width: 70,
      render: (isBest?: boolean) => (isBest ? <Tag color="gold">最高</Tag> : '-'),
    },
    { title: '交卷时间', dataIndex: 'submittedAt', width: 160 },
  ];

  return (
    <Drawer title={`我的成绩：${exam.title}`} open width={860} onClose={onClose}>
      <Spin spinning={loading}>
        <Table<MyExamAttemptItemDTO>
          rowKey="attemptId"
          size="small"
          pagination={false}
          dataSource={attempts}
          columns={attemptColumns}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedAttemptId ? [selectedAttemptId] : [],
            onChange: (keys) => setSelectedAttemptId(keys[0] as I.ID),
          }}
        />
      </Spin>

      <Spin spinning={resultLoading}>
        {result && (
          <>
            <Card style={{ marginTop: 16, textAlign: 'center' }}>
              <Space size={48}>
                <Statistic
                  title="得分"
                  value={result.score}
                  suffix={`/ ${result.totalScore}`}
                  valueStyle={{ color: result.pass ? '#3f8600' : '#cf1322' }}
                />
                <Statistic title="及格线" value={result.passScore} />
                <Statistic
                  title="用时"
                  value={formatDuration(result.durationSeconds)}
                />
              </Space>
            </Card>
            <List
              style={{ marginTop: 16 }}
              header={
                result.answers?.some((item) => item.correctAnswer)
                  ? '答题明细'
                  : '答题明细（本场考试未开放查看正确答案）'
              }
              dataSource={result.answers}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Space>
                      <b>第 {item.seq} 题</b>
                      <QuestionTypeTag type={item.type} />
                      {item.isCorrect ? (
                        <Tag color="green">+{item.gotScore} 分</Tag>
                      ) : (
                        <Tag color="red">0 / {item.score} 分</Tag>
                      )}
                    </Space>
                    <p style={{ margin: '8px 0' }}>{item.content}</p>
                    <div style={{ color: '#888' }}>
                      {(item.options || []).map((option, index) => (
                        <div key={index}>
                          {optionLetter(index)}. {option}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      我的答案：
                      {item.myAnswer ? (
                        <Tag color={item.isCorrect ? 'green' : 'red'}>
                          {item.myAnswer}
                        </Tag>
                      ) : (
                        <Tag>未作答</Tag>
                      )}
                      {item.correctAnswer && (
                        <span style={{ marginLeft: 12 }}>
                          正确答案：<Tag color="blue">{item.correctAnswer}</Tag>
                        </span>
                      )}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </>
        )}
      </Spin>
    </Drawer>
  );
};
